import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { SubSink } from 'subsink2';
import { ViewColumnComponent } from '../../../../view-column.component';
import { AgingPeriodFacade } from "../../facades/aging-period.facade";
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AgingPeriodStates } from '../../aging-period-controller/states';
import { AgingPeriodActions } from '../../aging-period-controller/actions';
import { AgingPeriodSelectors } from '../../aging-period-controller/selectors';
import {
  AgingPeriodContainerModel, bl_fi_aging_period_line_RowClass
} from 'blg-akaun-ts-lib';
import { Store } from "@ngrx/store";
import { SharedUtilitiesModule } from '../../../../utilities/shared-utilities.module';
import { GridOptions } from "ag-grid-enterprise";
import { ToastrService } from 'ngx-toastr';
import { SharedUtilitiesHelperFunctions } from '../../../../utilities/helper-functions';

interface LocalState {
  deactivateAdd: boolean;
  deactivateReturn: boolean;
  deactivateList: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-aging-period-edit',
  templateUrl: './aging-period-edit.component.html',
  styleUrls: ['./aging-period-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class AgingPeriodEditComponent extends ViewColumnComponent {
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => state.selectedIndex);
  createMainForm: FormGroup;
  prevIndex: number;
  @ViewChild(MatTabGroup, { static: true }) matTab: MatTabGroup;

  protected subs = new SubSink();
  protected compName = 'Aging Period Edit';
  protected index = 2;
  protected localState: LocalState;
  protected prevLocalState: any;
  form;
  formLine;
  // initial grid state
  gridApi;
  gridColumnApi;
  rowData = [];

  gridOptions: GridOptions = {
    statusBar: {
      statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent', key: 'totalAndFilter', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
          { statusPanel: 'agAggregationComponent', align: 'right' },
      ]
    },
    defaultColDef: {
        filter: 'agTextColumnFilter',
        floatingFilter: false,
        //minWidth: 140,
        //flex: 1,
        width: 100,
        sortable: true,
        resizable: true,
        suppressSizeToFit: false,
    },
    columnTypes: SharedUtilitiesHelperFunctions.columnTypes,
    suppressAggFuncInHeader: true,
    pagination: true,
    animateRows: true,
    sideBar: true,
    rowSelection: 'multiple',
    suppressRowClickSelection: false,
    multiSortKey: 'ctrl'
  }

  columnsDefs = [
    {
      headerName: "Start",
      field: "range_from",
      type: 'textColumn'
    },
    {
      headerName: "End",
      field: "range_to",
      type: 'textColumn'
    },
    {
      headerName: "Action",
      field: "guid",
      cellRenderer: (params: any) => {
        const button = document.createElement('button');
        button.innerHTML = 'Delete';
        button.className = 'btn-delete'; // Optional: add a class for styling
        button.addEventListener('click', () => {
            this.onDeleteRow(params.data);
        });
        return button;
      }
    }
  ];
  lines = [];
  periodType;
  showNumMonth = false;
  constructor(
    private toastr: ToastrService,
    private store: Store<AgingPeriodStates>,
    private viewColFacade: AgingPeriodFacade,
    private readonly componentStore: ComponentStore<LocalState>) {
    super();
  }

  ngOnInit() {
    this.subs.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subs.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);
    this.subs.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
    });
    this.form = new FormGroup({
      name: new FormControl('', Validators.required),
      period_type: new FormControl('', Validators.required),
      num_months: new FormControl('')
    });
    this.formLine = new FormGroup({
       range_from: new FormControl('', Validators.required),
       range_to: new FormControl('', Validators.required)
     });
    this.subs.sink = this.store.select(AgingPeriodSelectors.selectAgingPeriod).subscribe(a=>{
      if(a){
        this.periodType = a.period_type;
        if(this.periodType==='MONTH'){
          this.showNumMonth = true;
          this.form.patchValue({
            num_months: a.type
          });
        }else{
          this.showNumMonth = false;
        }
        this.form.patchValue({
          name: a.name,
          period_type: a.period_type,
          num_months: a.type
        });
      }

    })

    this.form.get('period_type')?.disable();
  }

  onDeleteRow(rowData: any): void {
    console.log(rowData)
    this.store.dispatch(AgingPeriodActions.deleteLine({ line: rowData }));
    this.createData();
  }


  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    this.gridColumnApi = params.columnApi;
    this.createData();
  }

  createData(){
    this.subs.sink = this.store.select(AgingPeriodSelectors.selectLines).subscribe(line=>{
      console.log('linee',line)
      if(line){
        this.lines = line;
        this.gridApi.setRowData(this.lines.filter(l=>l.status!=='DELETED'));
      }
    })
  }

  onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  onSave() {
    this.store.dispatch(AgingPeriodActions.editInit({ form: this.form.value }));
  }

  onAdd() {
    const line = new bl_fi_aging_period_line_RowClass();
    line.range_from =  this.formLine.value.range_from;
    line.range_to =  this.formLine.value.range_to;
    console.log(this.lines)
    const isOverlap = this.lines.filter(l=>l.status!=='DELETED').some(existingLine =>
      !(line.range_to < existingLine.range_from || line.range_from > existingLine.range_to)
    );

    if (isOverlap) {
      this.toastr.error(
        "The new period range overlaps with an existing range",
        'Error',
        {
          tapToDismiss: true,
          progressBar: true,
          timeOut: 1300
        }
      );
        return;
    }

    this.store.dispatch(AgingPeriodActions.addLine({ line }));
    this.formLine.reset();
    this.createData();
  }

  disableButton() {
    return this.form.invalid;
  }

  disableButtonAdd() {
    return this.formLine.invalid;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

}
